/**
 * Generic Document Editor - Section list for non-PRD/design documents
 * Story 297: Agent research docs visible in UI as dynamic tabs
 *
 * Simplified editor wrapping SectionList WITHOUT:
 * - Coverage dropdowns (no onCoversChange)
 * - Section type dropdowns (no onSectionTypeChange)
 * - Upstream anchors / metadata (no linkage data)
 * - Cross-document navigation callbacks
 * - Anchor auto-generation on insert (generic sections have no anchors)
 */

import { useState, useCallback, useRef, useMemo } from 'react';
import { SectionList } from '../editor/SectionList';
import { ButterBar } from '../editor/ButterBar';
import { useDocumentMutations } from '../../hooks/useDocumentMutations';
import { useRestoreSection } from '../../hooks/useSectionHistory';
import type { DocumentSection } from '../../types/document';

/** Info about a recently deleted section for undo */
interface DeletedSectionInfo {
  id: string;
  title: string;
}

interface GenericDocEditorProps {
  projectSlug: string;
  storyId: string;
  docType: string;
  sections: DocumentSection[];
  /** Called when a section is selected (for URL state persistence) */
  onSectionSelect?: (sectionKey: string) => void;
}

export function GenericDocEditor({
  projectSlug,
  storyId,
  docType,
  sections: initialSections,
  onSectionSelect,
}: GenericDocEditorProps) {
  const [sections, setSections] = useState<DocumentSection[]>(initialSections);
  const [deletedSection, setDeletedSection] = useState<DeletedSectionInfo | null>(null);
  const sectionSeqRef = useRef(Math.max(0, ...initialSections.map((s) => s.seq)));

  // Auto-activate body for first section when it's empty (just created via EmptyDocState)
  const initialActiveField = useMemo(() => {
    if (initialSections.length === 1 && !initialSections[0].body) {
      return { sectionKey: initialSections[0].section_key, field: 'body' as const };
    }
    return null;
  }, []); // Only compute on mount

  const {
    updateSection,
    createSection,
    deleteSection,
    reorderSections,
  } = useDocumentMutations(projectSlug, storyId, docType);

  const restoreSection = useRestoreSection(projectSlug, storyId, docType);

  const handleSave = useCallback(
    async (sectionKey: string, body: string) => {
      setSections((prev) =>
        prev.map((s) => (s.section_key === sectionKey ? { ...s, body } : s))
      );
      await updateSection.mutateAsync({ sectionKey, updates: { body } });
    },
    [updateSection]
  );

  const handleTitleChange = useCallback(
    (sectionKey: string, newTitle: string) => {
      setSections((prev) =>
        prev.map((s) => (s.section_key === sectionKey ? { ...s, title: newTitle } : s))
      );
      updateSection.mutate({ sectionKey, updates: { title: newTitle } });
    },
    [updateSection]
  );

  const handleReorder = useCallback(
    (activeKey: string, overKey: string) => {
      const activeIndex = sections.findIndex((s) => s.section_key === activeKey);
      const overIndex = sections.findIndex((s) => s.section_key === overKey);

      if (activeIndex === -1 || overIndex === -1) return;

      const newSections = [...sections];
      const [removed] = newSections.splice(activeIndex, 1);
      newSections.splice(overIndex, 0, removed);

      const reorderedSections = newSections.map((s, i) => ({ ...s, seq: i + 1 }));
      setSections(reorderedSections);

      const newOrder = reorderedSections.map((s) => s.section_key);
      reorderSections.mutate(newOrder);
    },
    [sections, reorderSections]
  );

  // Insert new section - no anchor auto-generation for generic docs
  const handleInsert = useCallback(
    (afterKey: string | null): string => {
      sectionSeqRef.current += 1;
      const newSeq = sectionSeqRef.current;
      const newKey = `section-${newSeq}`;
      const defaultTitle = 'New Section';

      const newSection: DocumentSection = {
        section_key: newKey,
        title: defaultTitle,
        body: '',
        seq: newSeq,
        anchor: null,
      };

      if (afterKey === null) {
        setSections([newSection, ...sections]);
      } else {
        const afterIndex = sections.findIndex((s) => s.section_key === afterKey);
        const newSections = [...sections];
        newSections.splice(afterIndex + 1, 0, newSection);
        setSections(newSections);
      }

      createSection.mutate(
        {
          title: newSection.title,
          body: newSection.body,
          section_key: newKey,
        },
        {
          onSuccess: (result) => {
            if (result.id) {
              setSections((prev) =>
                prev.map((s) =>
                  s.section_key === newKey ? { ...s, id: result.id } : s
                )
              );
            }
          },
        }
      );

      return newKey;
    },
    [sections, createSection]
  );

  const handleDelete = useCallback(
    (sectionKey: string) => {
      const section = sections.find((s) => s.section_key === sectionKey);
      if (section?.id) {
        setDeletedSection({ id: section.id, title: section.title });
      }
      setSections((prev) => prev.filter((s) => s.section_key !== sectionKey));
      deleteSection.mutate(sectionKey);
    },
    [sections, deleteSection]
  );

  const handleUndo = useCallback(() => {
    if (deletedSection) {
      restoreSection.mutate(deletedSection.id);
      setDeletedSection(null);
    }
  }, [deletedSection, restoreSection]);

  const handleDismissButterBar = useCallback(() => {
    setDeletedSection(null);
  }, []);

  return (
    <div className="generic-doc-editor" data-testid="generic-doc-editor">
      <SectionList
        sections={sections}
        docType={docType}
        onReorder={handleReorder}
        onSave={handleSave}
        onTitleChange={handleTitleChange}
        onInsert={handleInsert}
        onDelete={handleDelete}
        onSectionSelect={onSectionSelect}
        storyId={storyId}
        initialActiveField={initialActiveField}
        metadata={{}}
        /* No onCoversChange: hides coverage dropdown */
        /* No onSectionTypeChange: hides type dropdown */
        /* No upstreamAnchors: no linkage data */
      />

      {deletedSection && (
        <ButterBar
          message={`Deleted "${deletedSection.title}"`}
          onUndo={handleUndo}
          onDismiss={handleDismissButterBar}
          undoInProgress={restoreSection.isPending}
        />
      )}
    </div>
  );
}
